import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { usePrivacyPolicy, useRecordConsent } from '@/hooks/useCompliance'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'

const CONSENT_KEY = 'tigerflow_privacy_consent_version'

/** Routes the user must be able to read before accepting consent. */
const CONSENT_EXEMPT_PATHS = new Set(['/privacy-policy'])

function acceptedVersion(): string | null {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

// 2.13.2 — Privacy policy & data consent gate. On first login (or after a
// policy update) the user must accept two consent checkboxes before accessing
// the portal. Once accepted (recorded with timestamp + version), the children
// render. Privacy-policy is exempt so the linked policy page remains readable.
export function ConsentGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('compliance')
  const { pathname } = useLocation()
  const { data, isLoading } = usePrivacyPolicy()
  const record = useRecordConsent()
  const version = data?.data?.version
  const [accepted, setAccepted] = useState<string | null>(acceptedVersion())
  const [readPolicy, setReadPolicy] = useState(false)
  const [consentData, setConsentData] = useState(false)

  // Re-sync stored acceptance if it changes elsewhere.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAccepted(acceptedVersion())
  }, [])

  const needsConsent = useMemo(() => {
    if (isLoading) return false // wait for policy before gating
    if (!version) return false // no policy version published -> do not gate
    return accepted !== version
  }, [isLoading, version, accepted])

  const isExempt = CONSENT_EXEMPT_PATHS.has(pathname)

  function accept() {
    if (!readPolicy || !consentData) return
    record.mutate(
      { consent_type: 'privacy', granted: true },
      {
        onSuccess: () => {
          if (version) localStorage.setItem(CONSENT_KEY, version)
          setAccepted(version ?? null)
        },
        onError: e =>
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(e, t('consentGate.recordError')),
          }),
      }
    )
  }

  if (!needsConsent || isExempt) return <>{children}</>

  return (
    <div className='flex min-h-screen items-center justify-center bg-background p-4'>
      <Card className='w-full max-w-lg'>
        <CardHeader>
          <CardTitle>{t('consentGate.title')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            {t('consentGate.intro')}{' '}
            <Link
              to='/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary underline'
            >
              {t('consentGate.policyLink')}
            </Link>{' '}
            {t('consentGate.introSuffix')}
          </p>
          <label className='flex items-start gap-3 text-sm'>
            <Checkbox
              checked={readPolicy}
              onCheckedChange={v => setReadPolicy(!!v)}
            />
            <span>{t('consentGate.readAgree')}</span>
          </label>
          <label className='flex items-start gap-3 text-sm'>
            <Checkbox
              checked={consentData}
              onCheckedChange={v => setConsentData(!!v)}
            />
            <span>
              {t('consentGate.consentProcessing')}
            </span>
          </label>
          <Button
            className='w-full'
            onClick={accept}
            disabled={!readPolicy || !consentData || record.isPending}
          >
            {t('consentGate.accept')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default ConsentGate
